import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../../utils/networking";
import { useViewport } from "../../../utils/useViewport";
import { InactiveClientsResponse, InactiveRow } from "../../../interfaces";

const PRIMARY = "#9E1F63FF";
const CARD_BORDER = "1px solid #ececf0";

// "Inactive for" choices. Dated bands list only cases that HAVE a last
// interaction (`never=exclude`); "Never" lists the cases with no interaction
// recorded at all (`never=only`, no age gate). Keys double as the URL state.
type InactiveOption = {
  key: string;
  label: string;
  inactiveDays: number;
  maxInactiveDays?: number;
  never?: boolean;
};
const INACTIVE_OPTIONS: InactiveOption[] = [
  { key: "180", label: "180+ days", inactiveDays: 180 },
  { key: "180-365", label: "180–365 days", inactiveDays: 180, maxInactiveDays: 365 },
  { key: "365", label: "1 year+ (365+ days)", inactiveDays: 365 },
  { key: "730", label: "2 years+ (730+ days)", inactiveDays: 730 },
  { key: "never", label: "Never (no interaction recorded)", inactiveDays: 1, never: true },
];
const DEFAULT_OPTION = INACTIVE_OPTIONS[0];
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 350;

// `?inactiveDays=` / `?maxInactiveDays=` are read straight off the URL (the
// dashboard feed links in with them), so anything non-numeric or < 1 is
// ignored rather than forwarded to the server.
const parsePositiveInt = (raw: string | null): number | null => {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
};

// Resolve the URL (`?never=1`, or `?inactiveDays=N[&maxInactiveDays=M]`) to
// one of the dropdown options, synthesising an ad-hoc option for a band the
// list does not offer so the Select never shows an unknown value.
const optionFromParams = (params: URLSearchParams): InactiveOption => {
  if (params.get("never") === "1") {
    return INACTIVE_OPTIONS.find((o) => o.never) || DEFAULT_OPTION;
  }
  const min = parsePositiveInt(params.get("inactiveDays"));
  const max = parsePositiveInt(params.get("maxInactiveDays"));
  if (min === null) return DEFAULT_OPTION;
  const listed = INACTIVE_OPTIONS.find(
    (o) => !o.never && o.inactiveDays === min && (o.maxInactiveDays ?? null) === (max !== null && max > min ? max : null),
  );
  if (listed) return listed;
  return max !== null && max > min
    ? { key: `${min}-${max}`, label: `${min}–${max} days`, inactiveDays: min, maxInactiveDays: max }
    : { key: String(min), label: `${min}+ days`, inactiveDays: min };
};

// Days-inactive is null when the case has never had a single interaction —
// that is "worse" than any number of days, so it reads as "Never", not blank.
const daysInactiveCell = (row: InactiveRow) => {
  if (row.daysInactive === null || row.daysInactive === undefined) {
    return (
      <Chip
        size="small"
        label="Never"
        sx={{
          fontWeight: 700,
          backgroundColor: "#fdecea",
          color: "#c62828",
        }}
      />
    );
  }
  return (
    <Chip
      size="small"
      label={`${row.daysInactive} day${row.daysInactive === 1 ? "" : "s"}`}
      sx={{
        fontWeight: 700,
        backgroundColor: "#fbf5e6",
        color: "#8a6d00",
      }}
    />
  );
};

// Standalone, server-paginated view of GET /dashboard/inactive. The dashboard
// feed only shows a truncated preview of the same lane; this is the full list.
const InactiveClients = () => {
  const navigate = useNavigate();
  const { isPhone } = useViewport();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<InactiveRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [option, setOption] = useState<InactiveOption>(() =>
    optionFromParams(searchParams),
  );
  const { inactiveDays, maxInactiveDays, never } = option;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [search]);

  // Any filter change restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, option]);

  // Monotonic request id: a slow earlier response must never overwrite the
  // results of a newer one (fast typing / fast paging).
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
      inactiveDays: String(inactiveDays),
      never: never ? "only" : "exclude",
    });
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (maxInactiveDays) params.set("maxInactiveDays", String(maxInactiveDays));

    axiosInstance
      .get(`/dashboard/inactive?${params.toString()}`)
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        const data: InactiveClientsResponse = res.data || {};
        setRows(Array.isArray(data.rows) ? data.rows : []);
        setTotal(typeof data.total === "number" ? data.total : 0);
        setError(null);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load inactive clients:", err);
        setRows([]);
        setTotal(0);
        setError(
          err?.response?.status === 403
            ? "You are not authorized to view inactive clients."
            : "Could not load inactive clients. Please try again.",
        );
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [page, pageSize, inactiveDays, maxInactiveDays, never, debouncedSearch]);

  // Keep the Select honest when the URL carries a band we do not offer.
  const options = useMemo(
    () =>
      INACTIVE_OPTIONS.some((o) => o.key === option.key)
        ? INACTIVE_OPTIONS
        : [...INACTIVE_OPTIONS.filter((o) => !o.never), option, ...INACTIVE_OPTIONS.filter((o) => o.never)],
    [option],
  );

  const changeOption = (key: string) => {
    const next = options.find((o) => o.key === key);
    if (!next) return;
    setOption(next);
    const params = new URLSearchParams(searchParams);
    params.delete("inactiveDays");
    params.delete("maxInactiveDays");
    params.delete("never");
    if (next.never) params.set("never", "1");
    else {
      params.set("inactiveDays", String(next.inactiveDays));
      if (next.maxInactiveDays) params.set("maxInactiveDays", String(next.maxInactiveDays));
    }
    setSearchParams(params, { replace: true });
  };

  // Phones: the pager sits under a long card list, so a new page starts at
  // the top of the list.
  const listRef = useRef<HTMLDivElement>(null);
  const changePage = (newPage: number) => {
    setPage(newPage);
    const listTop = listRef.current;
    if (isPhone && listTop && listTop.getBoundingClientRect().top < 0) {
      listTop.scrollIntoView({ block: "start" });
    }
  };

  const emptyText = error
    ? "No results to show."
    : debouncedSearch
      ? `No inactive clients match "${debouncedSearch}".`
      : "No inactive clients.";

  const subtitle = never
    ? "Open clients with no interaction recorded at all, oldest case first."
    : maxInactiveDays
      ? `Open clients whose last interaction was between ${inactiveDays} and ${maxInactiveDays} days ago, most stale first.`
      : `Open clients whose last interaction was ${inactiveDays}+ days ago, most stale first.`;

  return (
    <Box sx={{ padding: "20px", textAlign: "left" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#2c2c34" }}>
        Inactive Clients
      </Typography>
      <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5, mb: 2 }}>
        {subtitle}
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2, flexWrap: "wrap" }}
        useFlexGap
        alignItems="center"
      >
        <TextField
          size="small"
          label="Search firm"
          placeholder="Firm name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="inactive-days-label">Inactive for</InputLabel>
          <Select
            labelId="inactive-days-label"
            label="Inactive for"
            value={option.key}
            onChange={(e) => changeOption(String(e.target.value))}
          >
            {options.map((o) => (
              <MenuItem key={o.key} value={o.key}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ color: "#8a8a93" }}>
          {loading ? "Loading…" : `${total} client${total === 1 ? "" : "s"}`}
        </Typography>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper
        ref={listRef}
        elevation={0}
        sx={{
          border: CARD_BORDER,
          borderRadius: "12px",
          overflow: "auto",
        }}
      >
        {isPhone ? (
          <Box>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <CircularProgress sx={{ color: PRIMARY }} />
              </Box>
            ) : rows.length === 0 ? (
              <Typography
                sx={{ textAlign: "center", color: "#8a8a93", py: 5 }}
              >
                {emptyText}
              </Typography>
            ) : (
              rows.map((row) => (
                // The whole card opens the case.
                <Box
                  key={row.case_id}
                  component={Link}
                  to={`/case?id=${row.case_id}`}
                  sx={{
                    display: "block",
                    padding: "12px 16px",
                    borderBottom: CARD_BORDER,
                    color: "inherit",
                    textDecoration: "none",
                    "&:active": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    justifyContent="space-between"
                  >
                    <Typography
                      className="thrive-user-text"
                      sx={{ color: PRIMARY, fontWeight: 600 }}
                    >
                      {row.firm_name || "Unknown"}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>{daysInactiveCell(row)}</Box>
                  </Stack>
                  <Typography variant="body2" sx={{ color: "#2c2c34" }}>
                    {row.owner_name || "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#8a8a93" }}>
                    {row.lastActivity
                      ? `Last interaction ${moment(row.lastActivity).format(
                          "MMM D, YYYY",
                        )}`
                      : "No interaction recorded"}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: "#2c2c34" } }}>
                <TableCell>Firm</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Last Interaction</TableCell>
                <TableCell>Days Inactive</TableCell>
                <TableCell align="right">Case</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress sx={{ color: PRIMARY }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{ textAlign: "center", color: "#8a8a93", py: 5 }}
                  >
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.case_id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/case?id=${row.case_id}`)}
                  >
                    <TableCell>
                      <Link
                        to={`/case?id=${row.case_id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          color: PRIMARY,
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        {row.firm_name || "Unknown"}
                      </Link>
                    </TableCell>
                    <TableCell>{row.owner_name || "—"}</TableCell>
                    <TableCell>
                      {row.lastActivity
                        ? moment(row.lastActivity).format("MMM D, YYYY")
                        : "No interaction recorded"}
                    </TableCell>
                    <TableCell>{daysInactiveCell(row)}</TableCell>
                    <TableCell align="right">
                      <Link
                        to={`/case?id=${row.case_id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          color: PRIMARY,
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        Open case
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => changePage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          // Phones: no rows-per-page select, so the page arrows fit.
          rowsPerPageOptions={isPhone ? [] : ROWS_PER_PAGE_OPTIONS}
        />
      </Paper>
    </Box>
  );
};

export default InactiveClients;
